/**
 * The client portal's half of the portal: talking to `/c/*`.
 *
 * Kept apart from `lib/api.ts` on purpose. That module speaks to `/api`, which
 * is behind Cloudflare Access and carries a staff Supabase session; this one
 * carries a client's session, obtained with their own password and emailed
 * code, and stored under its own key. The two must never share a token — a
 * client session sent to `/api` would be refused, and a staff one sent here
 * would be refused at sign-in anyway, but neither should get the chance.
 *
 * The session lives in localStorage so a client who signed in yesterday does
 * not have to again. Every read and write is guarded: storage can be blocked,
 * full or missing, and the portal has to work without it.
 */

import type { ClientStatus } from '@relay/shared/client-status';
import type { TicketPriority } from '@relay/shared/types';

const STORAGE_KEY = 'relay.client-session';

/** Refresh this long before expiry, so a request never races the clock. */
const REFRESH_MARGIN_MS = 60_000;

export type ClientSession = {
  readonly accessToken: string;
  readonly refreshToken: string;
  /** Epoch milliseconds. */
  readonly expiresAt: number;
  readonly email: string;
  readonly displayName: string;
};

/**
 * A session a reset code produced, held in memory and not yet stored.
 *
 * Stored only once the new password is set, so someone who closes the page
 * between the code and the password has not been left signed in without
 * knowing their password.
 */
export type PendingSession = SessionResponse;

type SessionResponse = {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly expiresIn: number;
  readonly email: string;
  readonly displayName: string;
};

export type SignInOutcome =
  | { readonly kind: 'signed-in'; readonly session: ClientSession }
  | { readonly kind: 'code-sent'; readonly email: string };

export type ClientProject = { readonly slug: string; readonly name: string };

export type ClientTicket = {
  readonly reference: string;
  readonly title: string;
  readonly summary: string | null;
  readonly status: ClientStatus;
  readonly statusLabel: string;
  readonly priority: TicketPriority;
  /** References of the unfinished reports this one is waiting on. */
  readonly blockedBy: readonly string[];
  readonly raisedAt: string;
  readonly updatedAt: string;
  readonly shared: boolean;
  readonly site: string | null;
  readonly raisedByMe: boolean;
  readonly raisedBy: string | null;
  /** What "done" means for the report, ticked as we get there. */
  readonly acceptanceCriteria: readonly { readonly text: string; readonly done: boolean }[];
};

export type ClientTicketPage = {
  readonly tickets: readonly ClientTicket[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly sites: readonly string[];
};

export type ClientTicketQuery = {
  readonly page: number;
  readonly search: string;
  readonly sort: 'newest' | 'oldest' | 'status';
  readonly status: ClientStatus | null;
  readonly site: string | null;
};

/** A step on a report's timeline. `body` is set on a reply we wrote, `author` null for "we". */
export type ClientEvent = {
  readonly at: string;
  readonly label: string;
  readonly author?: string | null;
  readonly body?: string;
};

/** A screenshot the client sent with a report. The bytes are `clientApi.screenshot`. */
export type ClientScreenshot = { readonly id: string; readonly capturedAt: string };

export class ClientApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ClientApiError';
  }
}

/* -------------------------------------------------------------------------- */
/* The stored session                                                          */
/* -------------------------------------------------------------------------- */

export function loadSession(): ClientSession | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<ClientSession>;
    if (typeof parsed.accessToken !== 'string' || typeof parsed.refreshToken !== 'string') return null;
    return parsed as ClientSession;
  } catch {
    return null;
  }
}

function saveSession(response: SessionResponse): ClientSession {
  const session: ClientSession = {
    accessToken: response.accessToken,
    refreshToken: response.refreshToken,
    expiresAt: Date.now() + response.expiresIn * 1_000,
    email: response.email,
    displayName: response.displayName,
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Signed in for this tab only. Better than refusing to sign in at all.
  }
  return session;
}

export function clearSession(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored, or nothing we can reach. Either way, nothing to clear.
  }
}

/* -------------------------------------------------------------------------- */
/* Requests                                                                    */
/* -------------------------------------------------------------------------- */

/** `blob` for image bytes; everything else is JSON. */
type Body = 'json' | 'blob';

async function send<T>(path: string, init: RequestInit = {}, token?: string, as: Body = 'json'): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers: {
        'content-type': 'application/json',
        ...(token === undefined ? {} : { authorization: `Bearer ${token}` }),
        ...(init.headers ?? {}),
      },
    });
  } catch {
    throw new ClientApiError(0, 'network', 'We could not reach Relay. Check your connection and try again.');
  }

  if (response.ok && as === 'blob') return (await response.blob()) as T;

  const body = (await response.json().catch(() => null)) as Record<string, unknown> | null;
  if (!response.ok) {
    const message = typeof body?.['message'] === 'string' ? body['message'] : 'Something went wrong. Try again.';
    const code = typeof body?.['error'] === 'string' ? body['error'] : 'request_failed';
    throw new ClientApiError(response.status, code, message);
  }
  return body as T;
}

/** Called when a session can no longer be used, so the page can go back to sign-in. */
let onSessionLost: () => void = () => undefined;

export function whenSessionLost(handler: () => void): void {
  onSessionLost = handler;
}

async function refreshed(session: ClientSession): Promise<ClientSession> {
  try {
    const response = await send<SessionResponse>('/c/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken: session.refreshToken }),
    });
    return saveSession(response);
  } catch (error) {
    clearSession();
    onSessionLost();
    throw error instanceof ClientApiError
      ? new ClientApiError(401, 'unauthenticated', 'Your session has ended. Sign in again.')
      : error;
  }
}

/**
 * A request as the signed-in client.
 *
 * Refreshes first when the token is about to expire, and once more if the
 * server says it already has — our clock and the server's can disagree.
 */
async function authed<T>(path: string, as: Body = 'json'): Promise<T> {
  let session = loadSession();
  if (session === null) {
    onSessionLost();
    throw new ClientApiError(401, 'unauthenticated', 'Sign in to see your reports.');
  }
  if (session.expiresAt - Date.now() < REFRESH_MARGIN_MS) session = await refreshed(session);

  try {
    return await send<T>(path, {}, session.accessToken, as);
  } catch (error) {
    if (!(error instanceof ClientApiError) || error.status !== 401) throw error;
    session = await refreshed(session);
    return send<T>(path, {}, session.accessToken, as);
  }
}

export const clientApi = {
  /** Which way this address signs in. By domain only; says nothing about whether an account exists. */
  async method(email: string): Promise<'staff' | 'client'> {
    const body = await send<{ method: string }>('/c/auth/method', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    return body.method === 'staff' ? 'staff' : 'client';
  },

  async signIn(email: string, password: string): Promise<SignInOutcome> {
    const body = await send<SessionResponse | { verificationRequired: true; email: string }>('/c/auth/signin', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if ('verificationRequired' in body) return { kind: 'code-sent', email: body.email };
    return { kind: 'signed-in', session: saveSession(body) };
  },

  /** Forgotten password, step one. Answers the same whether or not there is an account. */
  async resetStart(email: string): Promise<void> {
    await send('/c/auth/reset/start', { method: 'POST', body: JSON.stringify({ email }) });
  },

  /** Forgotten password, step two: check the code. The session it gives is not stored yet. */
  resetVerify(email: string, code: string): Promise<PendingSession> {
    return send<SessionResponse>('/c/auth/reset/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
  },

  /** Forgotten password, step three: set the new password, then store the session. */
  async setPassword(pending: PendingSession, password: string): Promise<ClientSession> {
    await send<null>('/c/auth/password', { method: 'POST', body: JSON.stringify({ password }) }, pending.accessToken);
    return saveSession(pending);
  },

  async verify(email: string, code: string): Promise<ClientSession> {
    const body = await send<SessionResponse>('/c/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
    return saveSession(body);
  },

  projects(): Promise<{ projects: readonly ClientProject[] }> {
    return authed('/c/projects');
  },

  tickets(slug: string, query: ClientTicketQuery, pageSize: number): Promise<ClientTicketPage> {
    const params = new URLSearchParams({ page: String(query.page), pageSize: String(pageSize), sort: query.sort });
    // Omitted rather than sent empty, so the API sees "no filter".
    if (query.search.trim().length > 0) params.set('search', query.search.trim());
    if (query.status !== null) params.set('status', query.status);
    if (query.site !== null) params.set('site', query.site);
    return authed(`/c/projects/${encodeURIComponent(slug)}/tickets?${params.toString()}`);
  },

  history(
    slug: string,
    reference: string,
  ): Promise<{
    /** What was typed when it was reported. Null for one we raised ourselves. */
    readonly description: string | null;
    readonly events: readonly ClientEvent[];
    readonly screenshots: readonly ClientScreenshot[];
  }> {
    return authed(`/c/projects/${encodeURIComponent(slug)}/tickets/${encodeURIComponent(reference)}/history`);
  },

  /**
   * One screenshot's bytes. Fetched rather than put in an `<img src>`, because
   * the request needs the bearer token and an image element cannot send one.
   */
  screenshot(slug: string, reference: string, id: string): Promise<Blob> {
    return authed(
      `/c/projects/${encodeURIComponent(slug)}/tickets/${encodeURIComponent(reference)}/screenshots/${encodeURIComponent(id)}`,
      'blob',
    );
  },
};
